import React, { useMemo, useEffect } from 'react';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';

import InventoryGrid from './InventoryGrid';
import InventoryHeader from './InventoryHeader';
import ExtensionStatus from '../../../extension/ExtensionStatus';
import { SortOption } from '../Sell';
import { GroupedInventoryItem, SelectedGroupedItem } from '../types/Inventory';
import { RootState } from '../../../../store';
import { useInventory } from './hooks/useInventory';
import { groupInventoryItems } from './utils/inventory';
import { InventoryFilterState } from '../../../skin-db/database/filter/SkinDbFilter';

type InventoryProps = {
  steamAccountId?: string;
  steamAccountSteamId?: string;
  tradeable?: boolean;
  filters: InventoryFilterState;
  selectedItems: SelectedGroupedItem[];
  onToggleSelectItem: (item: GroupedInventoryItem) => void;
  sortBy: SortOption;
  searchTerm: string;
  onSearchChange: (val: string) => void;
  onSortChange: (val: SortOption) => void;
  onUpdateFilters: (f: InventoryFilterState) => void;
  onResetFilters: () => void;
  onItemsSoldRef?: React.MutableRefObject<((assetIds: string[]) => void) | null>;
};

const Inventory: React.FC<InventoryProps> = (props) => {
  const currencySymbol = useSelector((state: RootState) => state.currency.symbol);

  const { items, loading, manualCooldown, isExtensionInstalled, loadInventory, removeItemsFromState } = useInventory(
      props.tradeable !== false ? props.steamAccountId : undefined,
      props.tradeable !== false ? props.steamAccountSteamId : undefined,
      props.filters,
      props.sortBy,
      props.searchTerm
  );

  useEffect(() => {
    if (props.onItemsSoldRef) {
      props.onItemsSoldRef.current = removeItemsFromState;
    }
    return () => {
      if (props.onItemsSoldRef) {
        props.onItemsSoldRef.current = null;
      }
    };
  }, [removeItemsFromState, props.onItemsSoldRef]);

  const groupedItems = useMemo(() => groupInventoryItems(items), [items]);

  return (
      <Box className="flex flex-col h-[87vh]">
        <ExtensionStatus variant="simple" className="lg:hidden mb-2" />
        <InventoryHeader
            {...props}
            currencySymbol={currencySymbol}
            loading={loading}
            manualCooldown={manualCooldown}
            isExtensionInstalled={isExtensionInstalled}
            onRefresh={() => loadInventory(true)}
        />
        <div className="flex-1 overflow-auto pr-1">
          <InventoryGrid
              loading={loading}
              groupedItems={groupedItems}
              selectedItems={props.selectedItems}
              currencySymbol={currencySymbol}
              onToggleSelectItem={props.onToggleSelectItem}
              onResetFilters={props.onResetFilters}
              tradeable={props.tradeable}
          />
        </div>
      </Box>
  );
};

export default Inventory;